import { useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import {
    GET_GENERATION_BASIS,
    SET_GENERATION_BASIS_DECISION,
    SET_GENERATION_BASIS_MODE,
} from '@/graphql/generation-basis-queries'
import {
    BASIS_ACTION_FAILED_MESSAGE,
    BASIS_CHANGED,
    BASIS_ITEM_UNKNOWN,
    BASIS_ITEM_UNKNOWN_MESSAGE,
    BASIS_UNAVAILABLE,
    DEFAULT_BASIS_NOUN,
    basisErrorMessageOf,
    basisRequestOf,
    basisSignatureOf,
    errorCodeOf,
    isBasisActive,
} from './basisUtils'
import type {
    GenerationBasisData,
    GenerationBasisDecisionValue,
    GenerationBasisInput,
    GenerationBasisMode,
    GenerationBasisPurpose,
} from './types'

interface GenerationBasisQueryData {
    generationBasis: GenerationBasisData
}

export type GenerationBasisStatus = 'idle' | 'loading' | 'error' | 'off' | 'ready'

export interface GenerationBasisController {
    /** What the sources are called in messages, such as "audience insight" */
    noun: string
    campaignId: string | null
    /** idle: not read (inactive); off: no source is switched on; ready: sources and items are shown */
    status: GenerationBasisStatus
    basis: GenerationBasisData | null
    /** Generate without insight for the next request */
    skip: boolean
    setSkip: (skip: boolean) => void
    /** True while the basis is being read or changed: a generation must not start, so it never sends a stale hash */
    blocked: boolean
    /** A notice about the last change or refused generation, in plain words */
    message: string | null
    /** The `basis` argument to send with a generation; undefined while the feature is off */
    request: GenerationBasisInput | undefined
    /** What a generation made now would be based on (see basisSignatureOf) */
    signature: string | null | undefined
    decide: (source: string, itemKey: string, decision: GenerationBasisDecisionValue | null) => Promise<void>
    approveAll: (source: string) => Promise<void>
    setMode: (source: string, mode: Exclude<GenerationBasisMode, 'off'>) => Promise<void>
    /** True while a change of the user is being saved */
    changing: boolean
    reload: () => Promise<void>
    /** Reads the basis again and returns it; null when it cannot be read */
    read: () => Promise<GenerationBasisData | null>
    clearMessage: () => void
    /** Reloads the basis after a refused generation; returns the message to show, or null for another error */
    handleGenerationError: (error: unknown) => string | null
    reset: () => void
}

interface UseGenerationBasisOptions {
    campaignId: string | null | undefined
    purpose: GenerationBasisPurpose
    /** Read the basis only while the screen that generates is shown */
    active?: boolean
    /** What the sources are called in messages; the audience insight unless a screen says otherwise */
    noun?: string
    /**
     * Keeps "generate without insight" with the caller (for example with a built set) instead of in this hook, so
     * the choice survives leaving and re-entering the screen.
     */
    skip?: boolean
    onSkipChange?: (skip: boolean) => void
}

/**
 * What the next generation of a purpose is based on, for a licence: the sources and their items, the user's choices
 * (approve, exclude, mode, generate without insight) and the hash to send with the request. After every change the
 * basis is read again, and `blocked` stays true until that read is done, so a generation never goes out with a hash
 * the user has not seen.
 */
export const useGenerationBasis = ({
    campaignId,
    purpose,
    active = true,
    noun = DEFAULT_BASIS_NOUN,
    skip: controlledSkip,
    onSkipChange,
}: UseGenerationBasisOptions): GenerationBasisController => {
    const enabled = active && !!campaignId
    const [ownSkip, setOwnSkip] = useState(false)
    const controlled = controlledSkip !== undefined
    const skip = controlled ? controlledSkip : ownSkip
    const setSkip = (value: boolean) => {
        if (!controlled) setOwnSkip(value)
        onSkipChange?.(value)
    }
    const [pending, setPending] = useState(0)
    const [message, setMessage] = useState<string | null>(null)
    const { data, loading, error, refetch } = useQuery<GenerationBasisQueryData>(GET_GENERATION_BASIS, {
        variables: { campaignId, purpose },
        skip: !enabled,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true,
    })
    const [saveDecision] = useMutation(SET_GENERATION_BASIS_DECISION)
    const [saveMode] = useMutation(SET_GENERATION_BASIS_MODE)

    const basis = enabled && !error ? (data?.generationBasis ?? null) : null
    let status: GenerationBasisStatus = 'idle'
    if (enabled) {
        if (error) status = 'error'
        else if (!basis) status = 'loading'
        else status = isBasisActive(basis) ? 'ready' : 'off'
    }

    const reload = async () => {
        if (!enabled) return
        try {
            await refetch()
        } catch {
            // the failed read is shown through the query error
        }
    }

    const read = async () => {
        if (!enabled) return null
        try {
            const result = await refetch()
            return result.data?.generationBasis ?? null
        } catch {
            return null
        }
    }

    /** Runs a change, then reads the basis again; the generation stays blocked meanwhile. */
    const change = async (action: () => Promise<unknown>) => {
        setPending((count) => count + 1)
        setMessage(null)
        try {
            await action()
        } catch (caught) {
            setMessage(
                errorCodeOf(caught) === BASIS_ITEM_UNKNOWN
                    ? BASIS_ITEM_UNKNOWN_MESSAGE
                    : (basisErrorMessageOf(caught, noun) ?? BASIS_ACTION_FAILED_MESSAGE),
            )
        }
        await reload()
        setPending((count) => count - 1)
    }

    const decide = (source: string, itemKey: string, decision: GenerationBasisDecisionValue | null) =>
        change(() => saveDecision({ variables: { campaignId, source, itemKey, decision } }))

    const approveAll = (source: string) => {
        const proposed = (basis?.items ?? []).filter((item) => item.source === source && item.state === 'proposed')
        return change(async () => {
            for (const item of proposed) {
                await saveDecision({ variables: { campaignId, source, itemKey: item.key, decision: 'APPROVED' } })
            }
        })
    }

    const setMode = (source: string, mode: Exclude<GenerationBasisMode, 'off'>) =>
        change(() => saveMode({ variables: { campaignId, source, mode } }))

    const handleGenerationError = (caught: unknown) => {
        const code = errorCodeOf(caught)
        if (code !== BASIS_CHANGED && code !== BASIS_UNAVAILABLE) return null
        const text = basisErrorMessageOf(caught, noun)
        setMessage(text)
        reload()
        return text
    }

    return {
        noun,
        campaignId: campaignId ?? null,
        status,
        basis,
        skip,
        setSkip,
        blocked: status === 'loading' || status === 'error' || loading || pending > 0,
        message,
        request: basisRequestOf(basis, skip),
        signature:
            status === 'idle' || status === 'loading' || status === 'error' ? undefined : basisSignatureOf(basis, skip),
        decide,
        approveAll,
        setMode,
        changing: pending > 0,
        reload,
        read,
        clearMessage: () => setMessage(null),
        handleGenerationError,
        reset: () => {
            if (!controlled) setOwnSkip(false)
            setMessage(null)
        },
    }
}
